import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import PageAuthLoader from "@/components/PageAuthLoader";
import { LOGIN } from "@/routes";
import { useTheme } from "@/contexts/PreferencesContext";

/** Paths that are publicly accessible even inside the authenticated AppShell. */
const PUBLIC_CONTENT_PREFIXES = ["/stash/", "/stuff/", "/collections/"];

/**
 * Route guard that requires authentication
 * Redirects to getting started if user is not authenticated
 * Saves the intended destination for post-login redirect
 * Can be used as a route element with <Outlet /> for nested routes
 *
 * Public content paths (/stash/:id, /stuff/:id) are allowed through
 * without auth so they remain shareable while still rendering inside
 * the AppShell stack (preserving scroll position for logged-in users).
 */
export const RequireAuth = ({ children }: { children?: React.ReactNode } = {}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { resetForcedTheme } = useTheme();

  const isPublicContent = PUBLIC_CONTENT_PREFIXES.some(prefix =>
    location.pathname.startsWith(prefix),
  );

  // Reset forced theme when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      resetForcedTheme();
    }
  }, [isAuthenticated, resetForcedTheme]);

  // Show loading state while checking auth status
  if (isLoading) {
    return <PageAuthLoader />;
  }

  // Redirect to login if not authenticated (unless it's public content)
  // Save the current location so we can redirect back after login
  if (!isAuthenticated && !isPublicContent) {
    return (
      <Navigate
        to={LOGIN()}
        replace
        state={{ from: location.pathname + location.search + location.hash }}
      />
    );
  }
  // Support both direct children and nested routes
  return children ? <>{children}</> : <Outlet />;
};
